/**
 * Lauremonté cart glue.
 *
 * The drawer itself, the line updates and the section re-rendering are
 * Horizon's; this module only connects the parts of the storefront that
 * are Lauremonté's own and would otherwise not know the drawer exists:
 *
 *   - the header bag, which shipped as a plain link to /cart
 *   - the header count, which is not Horizon's `cart-icon` component
 *
 * Add-to-cart is not handled here. Product forms are wrapped in
 * Horizon's `<product-form-component>` instead, so they go through the
 * same tested path as every other add in the theme.
 */

import { StandardEvents } from '@shopify/events';

/** @returns {(HTMLElement & { open(): void; close(): void; isOpen: boolean }) | null} */
function getDrawer() {
  const drawer = document.querySelector('#cart-drawer');
  // A drawer that has not upgraded yet has no open(); treating that as
  // "no drawer" is what lets the header fall back to navigating.
  return drawer && typeof (/** @type {any} */ (drawer).open) === 'function'
    ? /** @type {any} */ (drawer)
    : null;
}

/**
 * The bag in the header is an anchor to /cart on purpose: with no
 * JavaScript, a closed drawer or a failed upgrade, it still goes
 * somewhere useful. Only once a working drawer is on the page does the
 * click get taken over.
 */
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const trigger = target.closest('[data-ld-cart-open]');
  if (!trigger) return;

  // Let modified clicks (new tab, download, middle click) behave normally.
  if (event.defaultPrevented) return;
  if (/** @type {MouseEvent} */ (event).metaKey || /** @type {MouseEvent} */ (event).ctrlKey) return;
  if (/** @type {MouseEvent} */ (event).shiftKey || /** @type {MouseEvent} */ (event).button > 0) return;

  const drawer = getDrawer();
  if (!drawer) return;

  event.preventDefault();
  drawer.open();
});

/**
 * Keeps the header count in step with the cart.
 *
 * Horizon announces a line change before the request has settled, so the
 * count is read from the event's own promise where there is one and from
 * /cart.js otherwise. Both paths end at the same setter.
 *
 * @param {number} count
 */
function renderCount(count) {
  for (const node of document.querySelectorAll('[data-ld-cart-count]')) {
    // The bubble is markup-less at zero rather than a "0", so an empty
    // cart shows a plain bag the way it did before this module existed.
    node.textContent = count > 0 ? String(count) : '';
    node.toggleAttribute('hidden', count <= 0);
  }

  for (const label of document.querySelectorAll('[data-ld-cart-label]')) {
    const template = label.getAttribute('data-ld-cart-label');
    // The Liquid side renders the translation with a __COUNT__ sentinel
    // rather than the raw {{ count }}, which Liquid will not pass through
    // as a string argument.
    if (template) label.setAttribute('aria-label', template.replace('__COUNT__', String(count)));
  }
}

async function refreshCountFromServer() {
  try {
    const response = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return;
    const cart = await response.json();
    renderCount(cart.item_count ?? 0);
  } catch {
    /* A count that fails to refresh is not worth interrupting the page for. */
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const promise = /** @type {any} */ (event).promise;

  if (promise?.then) {
    promise
      .then(() => refreshCountFromServer())
      .catch(() => {
        /* handled by the theme's own error path */
      });
    return;
  }

  refreshCountFromServer();
});

// Restoring a page from the back/forward cache can show a stale count.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshCountFromServer();
});
